import { CartItem } from '../types';
import { OrderApiResponse } from './types';
import { getStoreInfo } from './api';

export async function createTakeawayOrder(
  items: CartItem[],
  customerName: string,
  total: number,
  itemNotes: { [key: string]: string },
  cartResult?: any,
  invoiceResult?: any
): Promise<OrderApiResponse> {
  try {
    const storeInfo = getStoreInfo();
    if (!storeInfo) {
      throw new Error('Store information not available. Please login first.');
    }

    // Log pour vérifier la configuration
    console.log('storeInfo.is_tax_included:', storeInfo.is_tax_included);
    console.log('storeInfo.item_tax:', storeInfo.item_tax);

    // Split address into parts for city
    const addressParts = storeInfo.address.split(',');
    const city = addressParts.length > 1 ? addressParts[1].trim() : '';

    // Calcul des taxes
    const taxRate = storeInfo.item_tax; // Taux brut (ex. 20 pour 20%)
    const taxRateDecimal = taxRate / 100; // Taux décimal (ex. 0.2)
    let subtotalHT: number;
    let taxAmount: number;
    let displayTotal: number;

    if (storeInfo.is_tax_included) {
      // Si la taxe est incluse dans le total
      subtotalHT = total / (1 + taxRateDecimal); // Extrait le HT
      taxAmount = total - subtotalHT; // Taxe incluse dans le total
      displayTotal = total; // Total TTC = total fourni
    } else {
      // Si la taxe n’est pas incluse
      subtotalHT = total; // Total fourni est HT
      taxAmount = subtotalHT * taxRateDecimal; // Calcule la taxe
      displayTotal = subtotalHT + taxAmount; // Total TTC
    }

    console.log('Calculs - subtotalHT:', subtotalHT, 'taxAmount:', taxAmount, 'displayTotal:', displayTotal);

    // 1. Add to cart (si non fourni)
    let finalCartResult = cartResult;
    if (!cartResult) {
      const cartData = {
        cart_id: "",
        cart_unique_token: storeInfo.server_token,
        delivery_types: "",
        destination_addresses: [{
          address: storeInfo.address,
          address_type: "destination",
          city: city,
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            country_phone_code: storeInfo.country_phone_code,
            email: "",
            first_name: "",
            image_url: "",
            last_name: "",
            name: customerName ? `Borne ${customerName}` : "Guest",
            phone: "",
            user_type: 7
          }
        }],
        id: storeInfo.store_id,
        is_guest_order: true,
        is_tax_included: storeInfo.is_tax_included,
        is_use_item_tax: false,
        order_details: [{
          items: items.map(item => ({
            item_id: item.menuItem.id,
            unique_id: Math.floor(Math.random() * 1000),
            item_name: item.menuItem.name,
            quantity: item.quantity,
            details: item.menuItem.description,
            image_url: [item.menuItem.image],
            item_price: item.menuItem.price,
            note_for_item: itemNotes[item.menuItem.id] || "",
            specifications: Object.entries(item.customizations).map(([specName, options]) => ({
              list: options.map(opt => ({
                _id: `${item.menuItem.id}-${specName}-${opt.id}`,
                main_specification_id: null,
                franchise_id: null,
                name: opt.name,
                price: opt.price,
                quantity: 1,
                is_default_selected: false,
                is_user_selected: true
              })),
              name: specName,
              price: 0,
              type: options.length > 1 ? 2 : 1
            })),
            tax: storeInfo.item_tax,
            tax_details: [],
            total_item_price: item.menuItem.price * item.quantity,
            total_item_tax: storeInfo.is_tax_included ? 0 : (item.menuItem.price * item.quantity) * taxRateDecimal,
            total_specification_tax: 0,
            product_id: item.menuItem.id,
            product_name: item.menuItem.name
          })),
          product_id: items[0]?.menuItem.id,
          product_name: items[0]?.menuItem.name,
          total_item_price: subtotalHT,
          total_item_tax: taxAmount,
          unique_id: Math.floor(Math.random() * 1000)
        }],
        pickup_addresses: [{
          address: storeInfo.address,
          address_type: "pickup",
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            name: storeInfo.name,
            email: storeInfo.email,
            phone: storeInfo.phone,
            country_phone_code: storeInfo.country_phone_code,
            image_url: storeInfo.image_url,
            user_type: 2
          }
        }],
        store_id: storeInfo.store_id,
        table_no: "0",
        timezone: "Europe/Paris",
        total_cart_price: subtotalHT,
        total_item_tax: taxAmount,
        user_id: "",
        user_type: 2
      };

      console.log('Sending cart data:', JSON.stringify(cartData, null, 2));

      const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(cartData)
      });

      if (!cartResponse.ok) {
        const errorText = await cartResponse.text();
        throw new Error(`Cart error: ${errorText}`);
      }

      finalCartResult = await cartResponse.json();
      console.log('Cart response:', finalCartResult);

      if (!finalCartResult.success) {
        throw new Error(finalCartResult.message || "Erreur lors de l'ajout au panier");
      }
    }

    // 2. Generate invoice (si non fourni)
    let finalInvoiceResult = invoiceResult;
    if (!invoiceResult) {
      const invoiceData = {
        cart_id: finalCartResult.cart_id,
        delivery_types: "",
        id: storeInfo.store_id,
        is_guest_order: true,
        is_store_place_order: false,
        is_tax_included: storeInfo.is_tax_included,
        is_use_item_tax: false,
        is_user_pick_up_order: true,
        order_type: 2,
        store_id: storeInfo.store_id,
        timezone: "Europe/Paris",
        total_cart_amout_without_tax: subtotalHT,
        total_cart_price: subtotalHT,
        total_distance: 0,
        total_item_count: items.length,
        total_item_tax: taxAmount,
        total_time: 0,
        user_id: finalCartResult.user_id || ""
      };

      console.log('Sending invoice data:', JSON.stringify(invoiceData, null, 2));

      const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(invoiceData)
      });

      if (!invoiceResponse.ok) {
        const errorText = await invoiceResponse.text();
        throw new Error(`Invoice error: ${errorText}`);
      }

      finalInvoiceResult = await invoiceResponse.json();
      console.log('Invoice response:', finalInvoiceResult);

      if (!finalInvoiceResult.success) {
        throw new Error(finalInvoiceResult.message || "Erreur lors de la génération de la facture");
      }
    }

    // Ajuster displayTotal avec les données de l’invoice si disponibles
    if (finalInvoiceResult && !storeInfo.is_tax_included) {
      displayTotal = finalInvoiceResult.order_payment.total;
      taxAmount = finalInvoiceResult.order_payment.total_store_tax_price || taxAmount;
      subtotalHT = finalInvoiceResult.order_payment.total_cart_price || subtotalHT;
      console.log('Ajustements après invoice - subtotalHT:', subtotalHT, 'taxAmount:', taxAmount, 'displayTotal:', displayTotal);
    }

    // 3. Process payment
    const paymentData = {
      cart_id: finalCartResult.cart_id,
      id: storeInfo.store_id,
      is_guest_order: true,
      is_payment_mode_cash: true,
      order_payment_id: finalInvoiceResult.order_payment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || ""
    };

    console.log('Sending payment data:', JSON.stringify(paymentData, null, 2));

    const paymentResponse = await fetch('https://api.restomi.fr/v3/api/user/pay_order_payment', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(paymentData)
    });

    if (!paymentResponse.ok) {
      const errorText = await paymentResponse.text();
      throw new Error(`Payment error: ${errorText}`);
    }

    const paymentResult = await paymentResponse.json();
    console.log('Payment response:', paymentResult);

    if (!paymentResult.success) {
      throw new Error(paymentResult.message || "Erreur lors du paiement");
    }

    // 4. Create final order
    const createOrderData = {
      cart_id: finalCartResult.cart_id,
      delivery_note: "",
      id: storeInfo.store_id,
      is_user_pick_up_order: true,
      order_payment_id: finalInvoiceResult.order_payment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || ""
    };

    console.log('Sending create order data:', JSON.stringify(createOrderData, null, 2));

    const createOrderResponse = await fetch('https://api.restomi.fr/v3/api/user/create_order', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(createOrderData)
    });

    if (!createOrderResponse.ok) {
      const errorText = await createOrderResponse.text();
      throw new Error(`Create order error: ${errorText}`);
    }

    const createOrderResult = await createOrderResponse.json();
    console.log('Create order response:', createOrderResult);

    if (!createOrderResult.success) {
      throw new Error(createOrderResult.message || "Erreur lors de la création de la commande");
    }

    // Retourne la réponse avec order_id inclus
    return {
      success: true,
      order_id: createOrderResult.order_id, // Ajout de order_id
      cart_id: finalCartResult.cart_id,
      user_id: finalCartResult.user_id,
      displayTotal,
      subtotal: subtotalHT,
      taxAmount,
      taxRate
    };
  } catch (error) {
    console.error('Error creating takeaway order:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Une erreur est survenue",
      displayTotal: total,
      subtotal: total,
      taxAmount: 0,
      taxRate: 0
    };
  }
}